import { useEffect, useRef, useState } from 'react';

const FEATURES = [
  {
    title: 'Custom Nail Art Design Process',
    desc: 'From initial consultation to final application, every step is intentional. We collaborate closely to translate your inspiration into a wearable work of art.',
    image: 'https://public.readdy.ai/ai/img_res/69b0704f519a3a7702870e4c2c6a7a8f.jpg',
    reverse: false,
  },
  {
    title: 'Wide Range of Finishes',
    desc: 'Gloss, matte, chrome, cat-eye, ombré, glitter — the palette is limitless. Each finish is applied with precision for a result that lasts.',
    image: 'https://readdy.ai/api/search-image?query=array%20of%20beautifully%20finished%20gel%20nails%20showcasing%20chrome%20mirror%20finish%2C%20matte%20nude%2C%20glitter%20and%20ombre%20effects%20on%20elegant%20hands%2C%20soft%20white%20background%2C%20luxury%20nail%20salon%2C%20professional%20beauty%20photography%2C%20warm%20gold%20lighting&width=700&height=520&seq=feat2&orientation=landscape',
    reverse: true,
  },
  {
    title: 'Hygiene & Professional Standards',
    desc: 'Every tool is sterilized. Every surface is clean. We uphold the highest professional standards so you can relax with complete confidence.',
    image: 'https://readdy.ai/api/search-image?query=pristine%20luxury%20nail%20salon%20workspace%20with%20sterilized%20professional%20tools%20neatly%20arranged%2C%20clean%20white%20marble%20surface%2C%20soft%20pink%20accents%2C%20gold%20nail%20tools%2C%20elegant%20and%20hygienic%20salon%20environment%2C%20high-end%20beauty%20studio%20photography&width=700&height=520&seq=feat3&orientation=landscape',
    reverse: false,
  },
  {
    title: 'Personalized Consultations',
    desc: 'No two clients are the same. We take time to understand your lifestyle, preferences, and aesthetic before a single product is opened.',
    image: 'https://readdy.ai/api/search-image?query=nail%20artist%20and%20client%20having%20a%20warm%20personalized%20consultation%20at%20a%20luxury%20nail%20salon%2C%20looking%20at%20nail%20design%20inspiration%20on%20phone%2C%20soft%20blush%20pink%20interior%2C%20gold%20accents%2C%20elegant%20and%20professional%20atmosphere%2C%20beauty%20photography&width=700&height=520&seq=feat4&orientation=landscape',
    reverse: true,
  },
];

export default function FeaturesSection() {
  const [visibleItems, setVisibleItems] = useState<boolean[]>(new Array(FEATURES.length).fill(false));
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = refs.current.map((ref, i) => {
      if (!ref) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => {
              const next = [...prev];
              next[i] = true;
              return next;
            });
          }
        },
        { threshold: 0.2 }
      );
      observer.observe(ref);
      return observer;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, []);

  return (
    <section id="features" className="bg-blush-light py-24 md:py-32 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-20">
          <span className="font-sans text-xs tracking-widest2 uppercase text-gold block mb-4">Our Craft</span>
          <h2 className="font-serif text-4xl md:text-5xl text-ink font-light">
            Built on Excellence
          </h2>
        </div>

        <div className="flex flex-col gap-20 md:gap-28">
          {FEATURES.map((feat, i) => (
            <div
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              className={`flex flex-col ${feat.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-10 md:gap-16 transition-all duration-800 ${
                visibleItems[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDuration: '700ms' }}
            >
              {/* Image */}
              <div className="w-full lg:w-[55%] h-72 md:h-96 overflow-hidden">
                <img
                  src={feat.image}
                  alt={feat.title}
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
                />
              </div>

              {/* Text */}
              <div className="w-full lg:w-[45%] flex flex-col justify-center">
                <div className="w-8 h-px bg-gold mb-6" />
                <h3 className="font-serif text-3xl md:text-4xl text-ink font-light leading-snug mb-5">
                  {feat.title}
                </h3>
                <p className="font-sans text-sm md:text-base text-ink-muted leading-relaxed">
                  {feat.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
